import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const COOKIE_MAX_AGE_DAYS = 90;

// Site Integration Audit item 9: this used to persist UTM params in
// sessionStorage, which is cleared the moment a visitor closes the tab and
// doesn't survive a hop to one of the firm's real subdomains either (each
// origin has its own sessionStorage). A real personal-injury lead very
// commonly closes the tab to "think about it" and comes back later --
// losing campaign attribution at that exact moment defeats the point of
// tracking it at all. A long-lived, first-party cookie survives both.
function setUtmCookie(name: string, value: string) {
    try {
        const expires = new Date(Date.now() + COOKIE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
    } catch (error) {
        console.warn(`Could not write UTM cookie "${name}".`, error);
    }
}

function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

/**
 * A custom hook to capture and retrieve UTM parameters from the URL.
 * Persists them as real, first-touch, long-lived first-party cookies (90
 * days) rather than sessionStorage -- first-touch means an already-set
 * cookie is never overwritten by a later visit, so the campaign that
 * genuinely brought this visitor to the site the first time is what a
 * lead record's marketing attribution reflects, even if they return later
 * via a different link or a direct visit.
 */
export const useUtm = () => {
    const location = useLocation();

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(location.search);
            UTM_PARAMS.forEach(param => {
                if (params.has(param) && !getCookie(param)) {
                    setUtmCookie(param, params.get(param)!);
                }
            });
        }
    }, [location.search]);

    const getUtmFields = (): { [key: string]: string } => {
        const fields: { [key: string]: string } = {};
        if (typeof window !== 'undefined') {
            try {
                UTM_PARAMS.forEach(param => {
                    const value = getCookie(param);
                    if (value) {
                        fields[param] = value;
                    }
                });
            } catch (error) {
                console.warn('Could not read UTM cookies.', error);
            }
        }
        return fields;
    };

    return { getUtmFields };
};
